import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { ApiError } from '../../lib/api';
import { bulkClearConfirmation } from '../../lib/portalAssets/bulk';
import { bulkClearPreview, bulkPortalAssets } from './staffApi';

// CLEAR title, tags and categories on ONE asset (Run E §6) — the single-asset twin of the bulk bar's Clear. STAFF
// ONLY (it names staff endpoints; the client portal never renders it).
//
// ⚠️ It goes through the SAME route as bulk, with one id: there is one clear path, so one audit shape and one place
// that decides what clearing means (updateAssetMetadata). It calls no AI and costs nothing.
//
// ⚠️ THE CONFIRMATION STATES THE EXPOSURE: whether a person wrote any of what is about to go. The preview is a read
// fired by the click that opens the panel (a mutation, never an effect or a query).

export function ClearAssetControl({ assetId, hasAnything, onDone }: { assetId: string; hasAnything: boolean; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const preview = useMutation({ mutationFn: () => bulkClearPreview([assetId]) });
  const run = useMutation({
    mutationFn: () => bulkPortalAssets([assetId], { op: 'clear' }),
    onSuccess: ({ results }) => {
      const r = results[0];
      if (r && r.outcome === 'ok') {
        setOpen(false);
        onDone();
      }
    },
  });
  const failed = run.data?.results[0];
  const message =
    failed && failed.outcome !== 'ok'
      ? failed.message
      : run.error
        ? `${run.error instanceof ApiError ? run.error.message : 'The request failed.'} Nothing has been confirmed — check the asset before trying again.`
        : null;
  const c = bulkClearConfirmation(1, preview.data?.preview ?? null);

  return (
    <div className="space-y-2" data-testid="clear-asset-control">
      <Button
        size="sm"
        variant="outline"
        disabled={!hasAnything || run.isPending}
        title={hasAnything ? undefined : 'There is no title, tag or category to clear.'}
        onClick={() => {
          setOpen(true);
          run.reset();
          preview.mutate();
        }}
      >
        Clear title, tags &amp; categories…
      </Button>
      {open && (
        <div className="space-y-2 rounded-md border border-border p-3 text-sm" role="group" aria-label="Confirm clear">
          <p className="font-medium text-foreground">{c.title}</p>
          {preview.isPending ? (
            <p className="text-muted-foreground">Checking what a person wrote…</p>
          ) : (
            <p className="font-medium text-foreground">{c.warning}</p>
          )}
          {c.body.map((line) => (
            <p key={line} className="text-muted-foreground">
              {line}
            </p>
          ))}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={c.destructive ? 'destructive' : 'default'}
              disabled={preview.isPending || run.isPending}
              onClick={() => run.mutate()}
            >
              {run.isPending ? 'Clearing…' : 'Clear'}
            </Button>
            <Button size="sm" variant="ghost" disabled={run.isPending} onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
          {message && (
            <p className="text-destructive" role="alert">
              {message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
